import { useState } from 'react'
import { ArrowLeftRight, Check, HandHeart, Repeat, X } from 'lucide-react'
import { HelpIcon, ApiError, Button, EndpointHint, LoadFailure } from '../../core/index.js'
import { useSwapCandidates, useWorkerBoard, useWorkerBoardActions, useWorkerCensusActions } from '../hooks.js'
import type { DraftAssignment, ShiftTypeKey, WorkerBoardView } from '../types.js'
import { describeDay, SHIFT_TYPE_LABELS } from './preferencesText.js'
import { CHANGE_STATUS_LABEL, PHASE_LABEL, SWAP_STATUS_CLASS, SWAP_STATUS_LABEL, describeApprovalReasons, describeLedgerEntry, describePostingShift, formatWhen } from './boardText.js'

const SHIFT_TYPES: readonly ShiftTypeKey[] = ['day', 'evening', 'night']

function isNotReleased(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409
}

function describeAssignment(entry: DraftAssignment): string {
  return `${describeDay(entry.date)} ${SHIFT_TYPE_LABELS[entry.shiftType].toLowerCase()} ${entry.startTime}–${entry.endTime}`
}

/** Request Swap: the ranked peers for one of the worker's shifts, one chosen, the exchange or the hand-over. */
function SwapForm({ workerId, period, shift, onDone }: { workerId: string; period: string; shift: DraftAssignment; onDone: () => void }) {
  const candidates = useSwapCandidates(workerId, shift.shiftId, period)
  const actions = useWorkerBoardActions(workerId, period)
  const [withWorkerId, setWithWorkerId] = useState<string>('')
  const chosen = candidates.data?.find((candidate) => candidate.workerId === withWorkerId) ?? null
  return (
    <form
      className="mt-2 flex flex-col gap-2 rounded border border-marine/15 bg-canvas/60 p-2.5 text-[11px]"
      onSubmit={(event) => {
        event.preventDefault()
        if (!chosen) {
          return
        }
        actions.requestSwap.mutate({ shiftId: shift.shiftId, withWorkerId: chosen.workerId, ...(chosen.theirShiftId ? { theirShiftId: chosen.theirShiftId } : {}) }, { onSuccess: onDone })
      }}
    >
      <div className="font-bold text-ink">Swap {describeAssignment(shift)}</div>
      {candidates.isPending ? (
        <p className="text-ink-mid">Finding who could take it…</p>
      ) : candidates.isError ? (
        <LoadFailure size="xs" message="The list could not be loaded." onRetry={() => void candidates.refetch()} retrying={candidates.isFetching} />
      ) : candidates.data.length === 0 ? (
        <p className="text-ink-mid">Nobody of your role is free to take it.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {candidates.data.map((candidate) => (
            <li key={candidate.workerId}>
              <label className="flex items-center gap-2">
                <input type="radio" name="peer" value={candidate.workerId} checked={withWorkerId === candidate.workerId} onChange={() => setWithWorkerId(candidate.workerId)} />
                <span className="font-bold text-ink">{candidate.name}</span>
                <span className="text-ink-mid">{candidate.theirShiftId ? `exchange for their ${candidate.theirShiftId.slice(11)} that day` : 'takes it outright'}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[10px] text-ink-mid">The person you choose is asked first; if they decline or do not answer in time, the next on the list is, without you seeing who. Your score is not touched either way.</p>
      {actions.requestSwap.isError && <p className="text-coral">{actions.requestSwap.error instanceof ApiError ? actions.requestSwap.error.message : 'The swap could not be asked for.'}</p>}
      <div className="flex gap-2">
        <Button icon={ArrowLeftRight} size="sm" type="submit" disabled={!chosen || actions.requestSwap.isPending}>
          {actions.requestSwap.isPending ? 'Asking…' : 'Ask to swap'}
        </Button>
        <button type="button" onClick={onDone} className="font-bold text-marine">
          Cancel
        </button>
      </div>
    </form>
  )
}

/** A change-window request on one shift: the other type that day, or a swap with a named peer. */
function ChangeForm({ workerId, period, shift, onDone }: { workerId: string; period: string; shift: DraftAssignment; onDone: () => void }) {
  const actions = useWorkerBoardActions(workerId, period)
  const [toShiftType, setToShiftType] = useState<ShiftTypeKey>(SHIFT_TYPES.find((type) => type !== shift.shiftType) ?? 'day')
  return (
    <form
      className="mt-2 flex flex-wrap items-end gap-2 rounded border border-marine/15 bg-canvas/60 p-2.5 text-[11px]"
      onSubmit={(event) => {
        event.preventDefault()
        actions.submitChange.mutate({ kind: 'shift_type', shiftId: shift.shiftId, toShiftType }, { onSuccess: onDone })
      }}
    >
      <span className="font-bold text-ink">Change {describeAssignment(shift)} to</span>
      <select className="rounded border border-marine/30 bg-white px-2 py-1 text-[11px]" value={toShiftType} onChange={(event) => setToShiftType(event.target.value as ShiftTypeKey)}>
        {SHIFT_TYPES.filter((type) => type !== shift.shiftType).map((type) => (
          <option key={type} value={type}>
            {SHIFT_TYPE_LABELS[type]}
          </option>
        ))}
      </select>
      <Button icon={Repeat} size="sm" type="submit" disabled={actions.submitChange.isPending}>
        Ask
      </Button>
      <button type="button" onClick={onDone} className="font-bold text-marine">
        Cancel
      </button>
      {actions.submitChange.isError && <p className="w-full text-coral">{actions.submitChange.error instanceof ApiError ? actions.submitChange.error.message : 'The request could not be sent.'}</p>}
      <p className="w-full text-[10px] text-ink-mid">Judged at the window's close with everyone else's, by reliability score; granted if a shift of that type is free that day.</p>
    </form>
  )
}

/**
 * The worker's side of the board on the Shifts tab (scheduling rules plan
 * phase C): their shifts now, with Request swap and, while the change
 * window is open, Change; the swaps and open shifts offered to them with
 * Accept and Decline; their own requests and what became of them. Nothing
 * shows until the month is released.
 */
export function WorkerBoardCard({ workerId, period = null }: { workerId: string; period?: string | null }) {
  const query = useWorkerBoard(workerId, period)
  const [open, setOpen] = useState<{ kind: 'swap' | 'change'; shift: DraftAssignment } | null>(null)
  if (query.isPending || (query.isError && isNotReleased(query.error))) {
    return null
  }
  if (query.isError || !query.data) {
    return <LoadFailure size="xs" message="Your schedule board could not be loaded." onRetry={() => void query.refetch()} retrying={query.isFetching} />
  }
  return <WorkerBoard view={query.data} workerId={workerId} open={open} setOpen={setOpen} />
}

function WorkerBoard({ view, workerId, open, setOpen }: { view: WorkerBoardView; workerId: string; open: { kind: 'swap' | 'change'; shift: DraftAssignment } | null; setOpen: (next: { kind: 'swap' | 'change'; shift: DraftAssignment } | null) => void }) {
  const actions = useWorkerBoardActions(workerId, view.period)
  const pending = [actions.answerChange, actions.answerSwap, actions.cancelSwap, actions.answerPosting].some((mutation) => mutation.isPending)
  const failure = [actions.answerChange, actions.answerSwap, actions.cancelSwap, actions.answerPosting].map((mutation) => mutation.error).find((error) => error) ?? null
  const askedSwaps = new Set(view.mySwaps.filter((swap) => swap.status === 'offered' || swap.status === 'needs_approval').map((swap) => swap.shiftId))
  const askedChanges = new Set(view.myChangeRequests.filter((request) => request.status === 'pending').map((request) => request.shiftId))
  const census = useWorkerCensusActions(workerId)
  const flexed = view.flexed ?? []
  const recalls = view.recalls ?? []
  const volunteerDates = new Set(view.volunteerDates ?? [])
  return (
    <div className="flex flex-col gap-3">
      {recalls.length > 0 && (
        <section className="border border-coral/50 bg-coral-lt p-3">
          <h2 className="text-xs font-extrabold text-ink">Your shift is yours again if you want it</h2>
          <ul className="mt-1.5 flex flex-col gap-1.5 text-[11px]">
            {recalls.map((seat) => (
              <li key={seat.shiftId} className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-ink">
                  {describeDay(seat.date)} {SHIFT_TYPE_LABELS[seat.shiftType].toLowerCase()} {seat.startTime}–{seat.endTime} on {seat.unitId} · the census came back
                </span>
                <span className="flex gap-1">
                  <Button icon={Check} size="sm" disabled={census.answerRecall.isPending} onClick={() => census.answerRecall.mutate({ shiftId: seat.shiftId, answer: 'accept' })}>
                    Take it back
                  </Button>
                  <Button icon={X} variant="secondary" size="sm" disabled={census.answerRecall.isPending} onClick={() => census.answerRecall.mutate({ shiftId: seat.shiftId, answer: 'decline' })}>
                    Let it go
                  </Button>
                </span>
              </li>
            ))}
          </ul>
          {census.answerRecall.isError && <p className="mt-1 text-[11px] text-coral">{census.answerRecall.error instanceof ApiError ? census.answerRecall.error.message : 'Your answer could not be recorded.'}</p>}
        </section>
      )}

      {(view.incomingSwaps.length > 0 || view.incomingChangeRequests.length > 0 || view.postings.length > 0) && (
        <section className="border border-coral/50 bg-coral-lt p-3">
          <h2 className="text-xs font-extrabold text-ink">Waiting for your answer</h2>
          <ul className="mt-1.5 flex flex-col gap-2 text-[11px]">
            {view.incomingSwaps.map((swap) => {
              const offer = swap.offers.find((entry) => entry.answer === null)
              return (
                <li key={swap.swapId} className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    <span className="font-bold text-ink">{swap.theirShiftId ? 'Swap shifts' : 'Take a shift'}</span>: {swap.shiftId.slice(0, 10)} {swap.shiftId.slice(11)}
                    {swap.theirShiftId && <> for your {swap.theirShiftId.slice(11)} that day</>}
                    {offer && <span className="text-ink-mid"> · answer by {formatWhen(offer.expiresAt)}</span>}
                  </span>
                  <span className="flex gap-1">
                    <Button icon={Check} size="sm" disabled={pending} onClick={() => actions.answerSwap.mutate({ swapId: swap.swapId, answer: 'accept' })}>
                      Accept
                    </Button>
                    <Button icon={X} variant="secondary" size="sm" disabled={pending} onClick={() => actions.answerSwap.mutate({ swapId: swap.swapId, answer: 'decline' })}>
                      Decline
                    </Button>
                  </span>
                </li>
              )
            })}
            {view.incomingChangeRequests.map((request) => (
              <li key={request.requestId} className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  <span className="font-bold text-ink">Change-window swap</span>: {request.shiftId.slice(0, 10)} {request.shiftId.slice(11)}
                  {request.theirShiftId && <> for your {request.theirShiftId.slice(11)}</>}
                  <span className="text-ink-mid"> · judged at the close</span>
                </span>
                <span className="flex gap-1">
                  <Button icon={Check} size="sm" disabled={pending} onClick={() => actions.answerChange.mutate({ requestId: request.requestId, answer: 'accept' })}>
                    Accept
                  </Button>
                  <Button icon={X} variant="secondary" size="sm" disabled={pending} onClick={() => actions.answerChange.mutate({ requestId: request.requestId, answer: 'decline' })}>
                    Decline
                  </Button>
                </span>
              </li>
            ))}
            {view.postings.map((posting) => (
              <li key={posting.postingId} className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  <span className="font-bold text-ink">Open shift</span>: {describePostingShift(posting.shift)}
                  <span className="text-ink-mid"> · {posting.phase === 'first_come' ? 'first to accept gets it' : posting.phase === 'locked' ? 'starts soon, first to accept gets it' : PHASE_LABEL[posting.phase].toLowerCase()}</span>
                </span>
                <span className="flex gap-1">
                  <Button icon={Check} size="sm" disabled={pending} onClick={() => actions.answerPosting.mutate({ postingId: posting.postingId, answer: 'accept' })}>
                    Take it
                  </Button>
                  {posting.phase === 'offers' && (
                    <Button icon={X} variant="secondary" size="sm" disabled={pending} onClick={() => actions.answerPosting.mutate({ postingId: posting.postingId, answer: 'decline' })}>
                      Pass
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="bg-white border border-marine/15 p-4">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="flex items-center gap-1 text-sm font-extrabold text-ink">Your shifts now<HelpIcon topic="schedule.worker-board" /></h2>
          <EndpointHint>GET /workers/{'{workerId}'}/schedule/board</EndpointHint>
        </div>
        <p className="mt-0.5 text-[11px] text-ink-mid leading-snug">
          {view.hours} hrs. {view.changeWindowOpen ? `The change window closes ${formatWhen(view.changeWindowClosesAt)}: ask for the other shift type on a day, judged at the close by reliability score.` : 'The change window has closed.'} Ask to swap a shift any time until a week before it.
        </p>
        <ul className="mt-2 divide-y divide-marine/10 text-[11px]">
          {view.assignments.map((shift) => (
            <li key={shift.shiftId} className="py-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-ink">{describeAssignment(shift)}</span>
                <span className="flex gap-1">
                  {view.changeWindowOpen && !askedChanges.has(shift.shiftId) && (
                    <Button icon={Repeat} variant="secondary" size="sm" onClick={() => setOpen(open?.shift.shiftId === shift.shiftId && open.kind === 'change' ? null : { kind: 'change', shift })}>
                      Change
                    </Button>
                  )}
                  {!askedSwaps.has(shift.shiftId) && (
                    <Button icon={ArrowLeftRight} variant="secondary" size="sm" onClick={() => setOpen(open?.shift.shiftId === shift.shiftId && open.kind === 'swap' ? null : { kind: 'swap', shift })}>
                      Swap
                    </Button>
                  )}
                </span>
              </div>
              {open?.shift.shiftId === shift.shiftId && open.kind === 'swap' && <SwapForm workerId={workerId} period={view.period} shift={shift} onDone={() => setOpen(null)} />}
              {open?.shift.shiftId === shift.shiftId && open.kind === 'change' && <ChangeForm workerId={workerId} period={view.period} shift={shift} onDone={() => setOpen(null)} />}
            </li>
          ))}
        </ul>
        {view.assignments.length === 0 && <p className="mt-1 text-[11px] text-ink-mid">No shifts this month.</p>}
        {flexed.filter((seat) => seat.status === 'held').length > 0 && (
          <div className="mt-2 border-t border-marine/10 pt-2 text-[11px]">
            <div className="text-[10px] font-bold uppercase tracking-wide text-gold">Released for low census</div>
            <ul className="mt-1 flex flex-col gap-0.5">
              {flexed.filter((seat) => seat.status === 'held').map((seat) => (
                <li key={seat.shiftId} className="text-ink-mid">
                  {describeDay(seat.date)} {SHIFT_TYPE_LABELS[seat.shiftType].toLowerCase()} on {seat.unitId} · the census fell, so this shift was released. It is yours first if the census comes back by {formatWhen(seat.recallUntil)}. This is not a call-out and your score is not touched.
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-2 border-t border-marine/10 pt-2 text-[11px]">
          <div className="text-[10px] font-bold uppercase tracking-wide text-marine">Low-census days</div>
          <p className="mt-0.5 text-ink-mid">If the census falls on a day you work, volunteers are released first. Mark the days you would happily take off.</p>
          <ul className="mt-1 flex flex-wrap gap-1">
            {[...new Set(view.assignments.map((shift) => shift.date))].sort().map((date) => (
              <li key={date}>
                <Button icon={HandHeart} variant={volunteerDates.has(date) ? 'primary' : 'secondary'} size="sm" disabled={census.volunteer.isPending || census.withdrawVolunteer.isPending} onClick={() => (volunteerDates.has(date) ? census.withdrawVolunteer.mutate(date) : census.volunteer.mutate(date))}>
                  {describeDay(date)}
                  {volunteerDates.has(date) ? ' · volunteered' : ''}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {(view.mySwaps.length > 0 || view.myChangeRequests.length > 0 || view.changes.length > 0) && (
        <section className="bg-white border border-marine/15 p-4 text-[11px]">
          <h2 className="text-sm font-extrabold text-ink">Your requests</h2>
          <ul className="mt-1.5 flex flex-col gap-1">
            {view.myChangeRequests.map((request) => (
              <li key={request.requestId} className="text-ink">
                {request.kind === 'shift_type' ? `${request.shiftId.slice(0, 10)}: to ${request.toShiftType}` : `${request.shiftId.slice(0, 10)}: swap with ${request.withWorkerId}`} · <span className="font-bold">{CHANGE_STATUS_LABEL[request.status]}</span>
                {request.reason && <span className="text-ink-mid"> ({request.reason.replace(/_/g, ' ')})</span>}
              </li>
            ))}
            {view.mySwaps.map((swap) => (
              <li key={swap.swapId} className="flex flex-wrap items-center justify-between gap-2 text-ink">
                <span>
                  Swap {swap.shiftId.slice(0, 10)} {swap.shiftId.slice(11)} · <span className={`font-bold ${SWAP_STATUS_CLASS[swap.status]}`}>{SWAP_STATUS_LABEL[swap.status]}</span>
                  {swap.approvalReasons.length > 0 && swap.status === 'needs_approval' && <span className="text-ink-mid"> ({describeApprovalReasons(swap.approvalReasons)})</span>}
                </span>
                {(swap.status === 'offered' || swap.status === 'needs_approval' || swap.status === 'completed') && (
                  <button type="button" disabled={pending} onClick={() => actions.cancelSwap.mutate(swap.swapId)} className="font-bold text-marine">
                    {swap.status === 'completed' ? 'Undo' : 'Withdraw'}
                  </button>
                )}
              </li>
            ))}
          </ul>
          {view.changes.length > 0 && (
            <ul className="mt-2 text-[10px] text-ink-mid">
              {view.changes.slice(0, 6).map((entry, index) => (
                <li key={`${entry.at}-${index}`}>
                  {formatWhen(entry.at)} · {describeLedgerEntry(entry, new Map())}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {failure && (
        <p role="alert" className="text-[11px] text-coral">
          {failure instanceof ApiError ? failure.message : 'That could not be done. Try again.'}
        </p>
      )}
    </div>
  )
}
